<template>
    <div v-allowed="'view_income'">
        <v-card elevation="0" class="pa-2">
            <v-row>
                <v-col cols="12" sm="12" md="6">
                    <div class="text-subtitle-2">
                        {{ $t('message.transactions_admin_plus.from') }}:
                    </div>
                    <div>
                        <DatePicker :show-time="false" :value="fromFilter" @input="$emit('update:from-filter', $event)"
                            :past-event="true">
                        </DatePicker>
                    </div>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                    <div class="text-subtitle-2">
                        {{ $t('message.transactions_admin_plus.to') }}:
                    </div>
                    <div>
                        <DatePicker :show-time="false" :value="toFilter" @input="$emit('update:to-filter', $event)"
                            :past-event="true">
                        </DatePicker>
                    </div>
                </v-col>
                <v-col cols="12" sm="12">
                    <v-autocomplete hide-details dense outlined v-if="customers && customers.length > 0"
                        :label="$t('message.transactions_admin_plus.customer')" :items="customers" item-text="userName"
                        item-value="stripeCustomerId" :value="customerFilter"
                        @input="$emit('update:customer-filter', $event)" clearable></v-autocomplete>
                </v-col>
            </v-row>
            <v-card-actions>
                <v-btn outlined elevation="0" small color="primary" @click="$emit('filter-invoices', false)">
                    <v-icon size="20" class="mr-1">mdi-filter-outline</v-icon>
                    {{ $t('message.transactions_admin_plus.filter') }}
                </v-btn>
                <v-btn elevation="0" small color="terciary" @click="$emit('filter-invoices', true)">
                    <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
                    {{ $t('message.transactions_admin_plus.report') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <div v-if="(stripeReady || paymentsModule == 'easypay') && loadingInvoices" class="text-center mt-2">
            <v-skeleton-loader
                type="list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line">
            </v-skeleton-loader>
        </div>
        <v-list dense v-if="invoicesTotal > 0">
            <div class="supporting-text" v-if="invoicesTotal >= 99">{{
                $t('message.transactions_admin_plus.showing_first_results', [invoicesTotal]) }}</div>
            <template v-for="(i, index) in invoices">
                <v-list-item :key="'invoice' + index" class="text-left">
                    <v-list-item-action>
                        <v-icon v-if="i.subscription" size="20">mdi-sync</v-icon>
                        <v-icon v-else size="20">mdi-credit-card-outline</v-icon>
                    </v-list-item-action>
                    <v-list-item-content text-xs-left>
                        <v-list-item-title>
                            <b class="mr-1">{{ currencySymbol + i.amountPaid }}</b>
                            <span v-if="i.productDisplay">{{ i.productDisplay }}</span>
                            <span v-else>
                                {{ i.subscription || (!i.product && !i.items) ? i.itemsDisplay : (i.product ? i.product
                                    :
                                    i.items
                                    +
                                    ' ' + $t('message.transactions_admin_plus.items')) }}
                                <v-icon size="15" color="accent" v-if="i.discount">
                                    mdi-tag-outline
                                </v-icon>
                            </span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            {{ i.userName ? i.userName : i.user }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ $d(new Date(i.date), 'long', $i18n.locale) }}
                        </v-list-item-title>
                        <v-list-item-title v-if="i.refunded">
                            <span class="error--text">{{ currencySymbol + i.refundedAmount }}
                                {{ $t('message.transactions_admin_plus.refunded') }}</span>
                        </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                                <v-btn elevation="0" v-on="on" icon small slot="activator" left>
                                    <v-icon size="20">mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item v-if="i.transaction" @click.stop="$emit('view-invoice-transaction', i)">
                                    <v-list-item-title>
                                        {{ $t('message.transactions_admin_plus.view') }}
                                    </v-list-item-title>
                                </v-list-item>
                                <v-list-item v-allowed="'issue_refund'" v-if="!i.refunded"
                                    @click.stop="$emit('show-refund-dialog', i)">
                                    <v-list-item-title>
                                        {{ $t('message.transactions_admin_plus.refund') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index + 1 < invoices.length"></v-divider>
            </template>
        </v-list>
        <v-card-text v-else-if="!loadingInvoices" class="text-center text-h6">
            {{ $t('message.transactions_admin_plus.select_time_start') }}
        </v-card-text>

        <!-- Refund Dialog -->
        <v-dialog :value="refundConfirmDialog" @input="$event ? null : $emit('close-refund-dialog')" max-width="600">
            <v-card style class="text-xs-left pa-3">
                <v-card-text>
                    <h2>{{ $t('message.transactions_admin_plus.refund_confirm') }}</h2>
                </v-card-text>
                <v-card-text>
                    <v-text-field :label="$t('message.transactions_admin_plus.amount')" outline :value="refundAmount"
                        @input="$emit('update:refund-amount', $event)"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-btn outlined elevation="0" color="primary" @click="$emit('refund')">{{ $t('message.transactions_admin_plus.refund')
                    }}</v-btn>
                    <v-btn elevation="0" @click="$emit('close-refund-dialog')">{{ $t('message.transactions_admin_plus.cancel') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import DatePicker from "@/components/inputs/DateTimePicker.vue";

export default {
    name: "ViewInvoices",
    components: {
        DatePicker
    },
    props: {
        academy: { type: Object, default: null },
        fromFilter: { default: null },
        toFilter: { default: null },
        customers: { type: Array, default: () => [] },
        customerFilter: { default: null },
        stripeReady: { type: Boolean, default: false },
        paymentsModule: { type: String, default: null },
        paymentsReady: { type: Boolean, default: false },
        loadingInvoices: { type: Boolean, default: true },
        invoicesTotal: { type: Number, default: 0 },
        invoices: { type: Array, default: () => [] },
        currencySymbol: { type: String, default: '$' },
        refundConfirmDialog: { type: Boolean, default: false },
        selectedInvoice: { default: null },
        refundAmount: { default: null }
    },
    emits: [
        'filter-invoices',
        'view-invoice-transaction',
        'show-refund-dialog',
        'refund',
        'close-refund-dialog',
        'update:from-filter',
        'update:to-filter',
        'update:customer-filter',
        'update:refund-amount'
    ]
};
</script>